import { defaultTranslations } from "../../config/constants";
import useCommandFlowStore from "../../store/commandFlowStore";
import { getTranslationFunction } from "../core/translationUtils";

export const getInitialMessage = (initialMessage) => {
  const t = getTranslationFunction();
  const defaultWelcome = [t("welcome"), t("helpTip")];
  return initialMessage.length > 0 ? initialMessage : defaultWelcome;
};
// Helper function to resolve prompts
export const resolvePrompt = async (prompt) => {
  return await Promise.resolve(prompt).catch(() => null);
};

// Helper function to resolve outputs
export const resolveOutput = async (output) => {
  const t = getTranslationFunction();
  return await Promise.resolve(output).catch(() => t("errorOccurred"));
};

export function getCommandOptions() {
  const translations = useCommandFlowStore.getState().translations;
  return {
    yesOption: translations.yes || defaultTranslations.yes,
    noOption: translations.no || defaultTranslations.no,
    exitCommands: translations.exitCommands || defaultTranslations.exitCommands,
    backCommands: translations.backCommands || defaultTranslations.backCommands,
  };
}

export function isYesInput(input) {
  const { yesOption } = getCommandOptions();
  return input.trim().toLowerCase() === yesOption.toLowerCase();
}

export function isNoInput(input) {
  const { noOption } = getCommandOptions();
  return input.trim().toLowerCase() === noOption.toLowerCase();
}

// Enhanced functions that support custom options
export function isCustomYesInput(input, customYesOption) {
  if (customYesOption) {
    return input.trim().toLowerCase() === customYesOption.toLowerCase();
  }
  return isYesInput(input);
}

export function isCustomNoInput(input, customNoOption) {
  if (customNoOption) {
    return input.trim().toLowerCase() === customNoOption.toLowerCase();
  }
  return isNoInput(input);
}

export function isBackCommand(input) {
  const { backCommands } = getCommandOptions();
  return backCommands.some((cmd) => input.trim().toLowerCase() === cmd);
}

export function isExitCommand(input) {
  const { exitCommands } = getCommandOptions();
  return exitCommands.includes(input.trim().toLowerCase());
}

export function isExitCommandInput(input) {
  const { exitCommands } = getCommandOptions();
  return exitCommands.some((cmd) => input.trim().toLowerCase() === cmd);
}

export function getCancelledOutput(prev, currentPrompt, trimmed, t) {
  return [
    ...prev,
    {
      type: "output",
      text: [
        { text: currentPrompt.trimEnd(), color: "output" },
        { text: " ", color: null },
        { text: trimmed, color: "input" },
      ],
    },
    { type: "output", text: t("commandCancelled") },
  ];
}

export function outputSuggestions({ error, suggestions }) {
  const addOutputWithTypingEffect =
    useCommandFlowStore.getState().addOutputWithTypingEffect;
  const setOutputWithDelay = useCommandFlowStore.getState().setOutputWithDelay;
  const t = getTranslationFunction();

  if (suggestions.length) {
    const lines = [
      { type: "output", text: `${error}` },
      {
        type: "output",
        text: t("didYouMean", {
          suggestions: suggestions.join(", "),
        }),
      },
    ];
    addOutputWithTypingEffect(lines);
  } else {
    const outputLines = [{ type: "output", text: error }];
    addOutputWithTypingEffect(outputLines);
  }
}

export function getCommandSuggestions(commands, trimmed) {
  return Object.entries(commands)
    .filter(([name, cmd]) => !cmd.hidden)
    .map(([name]) => name)
    .filter((name) => name.includes(trimmed) || name.startsWith(trimmed[0]));
}

// Utility function to delay output by ms milliseconds
export function delayOutput(ms, value) {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}
